/**
 * Single source of truth for public company data.
 *
 * Every public page (About, Contact, Footer, Header, SEO structured data) reads
 * from here — directly or through the `brand` re-export in `esanzaContent.ts` —
 * so company identity, legal name and contact details cannot drift apart.
 *
 * Runtime CMS values from `/api/public/website-content` may override display
 * copy, but they never redefine who the company is.
 */

export interface TeamMember {
  name: string;
  role: string;
  image: string;
  focus: string;
  linkedIn?: string;
}

export const company = {
  /** Public-facing brand name. */
  brandName: 'eSanza',
  /** Registered legal entity name. Do not shorten to "eSanza Ltd" in metadata. */
  legalName: 'eSanza Limited',
  /** Backwards-compatible alias for the brand name. */
  name: 'eSanza',
  domain: 'esanza.com',
  url: 'https://esanza.com',
  /**
   * Public support/enquiries address. Used for footers, contact forms and
   * structured data.
   */
  publicEmail: 'info@esanza.com',
  /**
   * Legal/registration correspondence address. Kept as an explicit field so a
   * registration-specific address can be configured without silently replacing
   * the public support address. Currently the same approved public address.
   */
  legalEmail: 'info@esanza.com',
  /** Backwards-compatible alias resolving to the public address. */
  email: 'info@esanza.com',
  /** Public phone number in international format. */
  publicPhone: '+250 788 635 555',
  /** Backwards-compatible alias for the public phone number. */
  phone: '+250 788 635 555',
  /** Where the company was founded and is registered. Not a market limit. */
  originCountry: 'Rwanda',
  originCountryCode: 'RW',
  registration: 'Founded and built in Rwanda.',
  /** Target market is international — location is an attribute, not a definition. */
  targetMarket: 'International',
  marketStatement: 'Designed for agricultural organisations across markets.',
  socials: {
    linkedin: '',
    twitter: '',
    youtube: '',
    instagram: '',
  },
} as const;

export const team: TeamMember[] = [
  {
    name: 'Lambert Ndacyayisaba',
    role: 'Co-Founder & CEO',
    image: '/images/team/lambert.png',
    focus:
      'Company leadership, technology strategy, product development and platform execution.',
    linkedIn: 'https://www.linkedin.com/in/ndacyayisaba-lambert-66625b231',
  },
  {
    name: 'Nicole Kalisa Umutoni',
    role: 'Co-Founder, Strategy & Partnerships',
    image: '/images/team/kalisa.jpeg',
    focus: 'Strategic growth, institutional relationships and partnerships.',
    linkedIn: 'https://www.linkedin.com/in/nicolekalisaumutoni',
  },
  {
    name: 'Uwodukunda Concilie',
    role: 'Co-Founder & COO',
    image: '/images/team/concilie.jpeg',
    focus: 'Operations, organisational execution and operational systems.',
    linkedIn: 'https://www.linkedin.com/in/uwodukunda',
  },
  {
    name: 'Dr. Ir. Jimmy Nsenga',
    role: 'Co-Founder & AI R&D Lead',
    image: '/images/team/jimmy.png',
    focus: 'AI research, agricultural intelligence, data science and technical advisory.',
    linkedIn: 'https://www.linkedin.com/in/jimmynsenga',
  },
];

/** Founders, derived from the canonical team list for structured data. */
export const founders = team
  .filter((member) => member.role.toLowerCase().includes('founder'))
  .map((member) => ({ name: member.name, role: member.role }));

export const mission =
  'To make agricultural operations smarter, more transparent and more connected by combining field data, AI-assisted decision support, traceability and market infrastructure.';

export const vision =
  'A connected agricultural economy where every farm can be managed intelligently, every product can be trusted, and every participant can access better information and markets.';

/**
 * Deployable status wording. Deliberately conservative: the platform runs,
 * field validation is underway. Nothing here may claim measured yield gains,
 * paying-customer counts or AI accuracy figures that are not evidenced in the
 * repository.
 */
export const status = {
  platform: 'Working platform.',
  field: 'Field validation underway.',
} as const;

export default company;
